import React, { useState } from "react";
import { Mail, MapPin, Phone } from "lucide-react";

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    companyName: "",
    email: "",
    serviceInterest: "",
    requirement: ""
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.companyName.trim()) {
      newErrors.companyName = "Company name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.serviceInterest.trim()) {
      newErrors.serviceInterest = "Service interest is required";
    }

    if (!formData.requirement.trim()) {
      newErrors.requirement = "Requirement description is required";
    } else if (formData.requirement.trim().length < 10) {
      newErrors.requirement = "Requirement must be at least 10 characters long";
    }

    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setIsSubmitting(true);

    // Create mailto link with form data
    const subject = `Contact Form: ${formData.serviceInterest}`;
    const body = `
Name: ${formData.name}
Company: ${formData.companyName}
Email: ${formData.email}
Service Interest: ${formData.serviceInterest}

Requirement:
${formData.requirement}
    `.trim();

    const mailtoLink = `mailto:otc@oasistechllc.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    // Open email client
    window.location.href = mailtoLink;
    
    // Reset form after a delay
    setTimeout(() => {
      setFormData({
        name: "",
        companyName: "",
        email: "",
        serviceInterest: "",
        requirement: ""
      });
      setErrors({});
      setIsSubmitting(false);
    }, 1000);
  };

  const handleEmailClick = () => {
    window.location.href = "mailto:otc@oasistechllc.com";
  };

  const handlePhoneClick = () => {
    window.location.href = "tel:+971503835430";
  };

  const handleSisterCompanyEmailClick = () => {
    window.location.href = "mailto:zhonglianzhihang@zlzh.cn";
  };

  const handleSisterCompanyWebsiteClick = () => {
    window.open("http://www.zlzhsoft.com/", "_blank");
  };

  return (
    <section className="bg-[#f9fbff] py-16 px-6 md:px-12">
      {/* Header */}
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
          <span className="text-orange-500">««</span> Contact Us{" "}
          <span className="text-orange-500">»»</span>
        </h2>
        <p className="text-gray-600 mt-4">
          Are you ready to revamp your engineering process? Please contact us to
          discuss your needs
        </p>
      </div>

      {/* Contact Form and Info */}
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Left Side – Form */}
        <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
          <h3 className="text-2xl font-semibold text-gray-800 mb-6 border-b-2 border-blue-500 w-fit pb-1">
            Leave us a message
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-gray-700 font-medium mb-1">Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Please enter your name"
                className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.name ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-1">Company Name *</label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                placeholder="Please enter your company name"
                className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.companyName ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.companyName && (
                <p className="text-red-500 text-sm mt-1">{errors.companyName}</p>
              )}
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-1">Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Please enter your email address"
                className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.email ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-1">Service interest *</label>
              <input
                type="text"
                name="serviceInterest"
                value={formData.serviceInterest}
                onChange={handleChange}
                placeholder="Please enter your service interest"
                className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.serviceInterest ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.serviceInterest && (
                <p className="text-red-500 text-sm mt-1">{errors.serviceInterest}</p>
              )}
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-1">Requirement *</label>
              <textarea
                rows="4"
                name="requirement"
                value={formData.requirement}
                onChange={handleChange}
                placeholder="Please enter your requirement description"
                className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.requirement ? "border-red-500" : "border-gray-300"
                }`}
              ></textarea>
              {errors.requirement && (
                <p className="text-red-500 text-sm mt-1">{errors.requirement}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full font-medium py-3 rounded-md transition ${
                isSubmitting
                  ? "bg-blue-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              } text-white`}
            >
              {isSubmitting ? "Preparing Email..." : "Send a message"}
            </button>
          </form>
        </div>

        {/* Right Side – Contact Info */}
        <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
          <h3 className="text-2xl font-semibold text-gray-800 mb-6 border-b-2 border-blue-500 w-fit pb-1">
            Contact Information
          </h3>

          <ul className="space-y-6 text-gray-700">
            <li className="flex items-start gap-3">
              <Mail className="text-blue-600 w-5 h-5 mt-1" />
              <div>
                <p className="font-semibold">Send us an email</p>
                <button
                  onClick={handleEmailClick}
                  className="text-blue-600 hover:text-blue-800 hover:underline transition"
                >
                  otc@oasistechllc.com
                </button>
              </div>
            </li>

            <li className="flex items-start gap-3">
              <Phone className="text-blue-600 w-5 h-5 mt-1" />
              <div>
                <p className="font-semibold">Call us</p>
                <button
                  onClick={handlePhoneClick}
                  className="text-blue-600 hover:text-blue-800 hover:underline transition"
                >
                  +971 2-556-1960
                </button>
              </div>
            </li>

            <li className="flex items-start gap-3">
              <MapPin className="text-blue-600 w-5 h-5 mt-1" />
              <div>
                <p className="font-semibold">Abu Dhabi, UAE</p>
                <p>
                  Oasis Tech Consultancy-L.L.C-S.P.C <br />
                  Office #1301, Al Masaood Tower, Hamdan Bin Muhammad Street,
                  <br />
                  606 Al Danah, Abu Dhabi
                </p>
              </div>
            </li>
          </ul>

          {/* Additional Information */}
          <div className="mt-8 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-800 mb-2">Quick Response</h4>
            <p className="text-blue-700 text-sm">
              We typically respond to all inquiries within 24 hours during business days.
            </p>
          </div>

          {/* Sister Company Information */}
          <div className="mt-8 py-4 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              <h3 className="font-medium mb-3 text-gray-800">Parent Company:</h3>
              <div className="space-y-2">
                <p className="font-semibold">Zhonglian Zhihang (Beijing) Technology Co., Ltd.</p>
                <p className="leading-relaxed">
                  Room 805, Zhongyuan Technology Building, No. 53 Xiaoying North Road, 
                  Chaoyang District, Beijing
                </p>
                <p>
                  Email:{" "}
                  <button
                    onClick={handleSisterCompanyEmailClick}
                    className="text-blue-600 hover:text-blue-800 hover:underline transition"
                  >
                    zhonglianzhihang@zlzh.cn
                  </button>
                </p>
                <p>
                  Website:{" "}
                  <button
                    onClick={handleSisterCompanyWebsiteClick}
                    className="text-blue-600 hover:text-blue-800 hover:underline transition"
                  >
                    http://www.zlzhsoft.com/
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;